import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { Loader2, Search, Download, RefreshCw, Mail, Users, X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import ExcelJS from "exceljs";

interface Props {
  sessionToken: string;
}

interface UserInfo {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  created_at: string;
  last_sign_in_at: string | null;
  source: "registered" | "certificate" | "both";
}

const formatName = (name: string) =>
  name
    ? name
        .split(" ")
        .map((w: string) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
        .join(" ")
    : "—";

const UsersTab = ({ sessionToken }: Props) => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [sendMode, setSendMode] = useState<"all" | "selected" | "exclude">("all");
  const { toast } = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch registered users
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_users", sessionToken },
      });
      if (res.data?.error === "Session expired. Please log in again.") {
        toast({ title: "Session expired", variant: "destructive" });
        return;
      }
      const registeredUsers: UserInfo[] = (res.data?.users || []).map((u: any) => ({
        ...u,
        source: "registered" as const,
      }));

      // Fetch certificate requesters
      const certRes = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list", sessionToken },
      });
      const certRequests: any[] = certRes.data?.requests || [];

      // Merge: deduplicate by email
      const emailMap = new Map<string, UserInfo>();
      for (const u of registeredUsers) {
        if (u.email) emailMap.set(u.email.toLowerCase(), u);
      }
      for (const cr of certRequests) {
        const email = cr.email?.toLowerCase();
        if (!email) continue;
        if (emailMap.has(email)) {
          const existing = emailMap.get(email)!;
          existing.source = "both";
        } else {
          emailMap.set(email, {
            id: cr.id,
            email: cr.email,
            full_name: `${cr.first_name} ${cr.last_name}`,
            phone: cr.phone || "",
            created_at: cr.created_at,
            last_sign_in_at: null,
            source: "certificate",
          });
        }
      }

      setUsers(Array.from(emailMap.values()).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch {
      toast({ title: "Failed to load users", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = useMemo(() =>
    users.filter((u) => {
      const q = search.toLowerCase();
      return (
        u.full_name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q)
      );
    }),
    [users, search]
  );

  const handleSelectAll = (checked: boolean) => {
    if (sendMode === "selected") {
      setSelectedIds(checked ? new Set(filtered.map((u) => u.id)) : new Set());
    } else if (sendMode === "exclude") {
      setExcludedIds(checked ? new Set(filtered.map((u) => u.id)) : new Set());
    }
  };

  const toggleUser = (id: string) => {
    if (sendMode === "selected") {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else if (sendMode === "exclude") {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
  };

  const recipientEmails = useMemo(() => {
    if (sendMode === "all") return filtered.map((u) => u.email).filter(Boolean);
    if (sendMode === "selected") return filtered.filter((u) => selectedIds.has(u.id)).map((u) => u.email).filter(Boolean);
    if (sendMode === "exclude") return filtered.filter((u) => !excludedIds.has(u.id)).map((u) => u.email).filter(Boolean);
    return [];
  }, [sendMode, filtered, selectedIds, excludedIds]);

  const handleExport = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Users");
    ws.columns = [
      { header: "Full Name", key: "full_name", width: 25 },
      { header: "Email", key: "email", width: 30 },
      { header: "Phone", key: "phone", width: 18 },
      { header: "Source", key: "source", width: 15 },
      { header: "Registered At", key: "created_at", width: 22 },
      { header: "Last Sign In", key: "last_sign_in_at", width: 22 },
    ];
    filtered.forEach((u) => {
      ws.addRow({
        full_name: formatName(u.full_name),
        email: u.email,
        phone: formatPhoneNumber(u.phone),
        source: u.source === "both" ? "Registered + Certificate" : u.source === "certificate" ? "Certificate Requester" : "Registered",
        created_at: new Date(u.created_at).toLocaleString(),
        last_sign_in_at: u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "Never",
      });
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendBulkEmail = async () => {
    if (!bulkSubject.trim() || !bulkBody.trim()) {
      toast({ title: "Subject and body are required", variant: "destructive" });
      return;
    }
    if (recipientEmails.length === 0) {
      toast({ title: "No recipients selected", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "send_bulk_email",
          sessionToken,
          subject: bulkSubject,
          emailBody: bulkBody,
          recipientEmails,
        },
      });
      if (res.data?.error) throw new Error(res.data.error);
      toast({ title: "Emails sent!", description: `Sent to ${res.data?.sentCount || 0} recipients.` });
      setBulkSubject("");
      setBulkBody("");
      setShowBulkEmail(false);
    } catch (err) {
      const error = err as Error;
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const showCheckboxes = showBulkEmail && sendMode !== "all";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} disabled={filtered.length === 0}>
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowBulkEmail(!showBulkEmail)}>
                <Mail className="w-4 h-4 mr-1" /> Bulk Email
              </Button>
            </div>
          </div>

          {showBulkEmail && (
            <Card className="border-primary/20">
              <CardContent className="pt-4 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <Label className="text-sm font-medium">Send to:</Label>
                  <div className="flex gap-1">
                    {(["all", "selected", "exclude"] as const).map((mode) => (
                      <Button
                        key={mode}
                        size="sm"
                        variant={sendMode === mode ? "default" : "outline"}
                        onClick={() => {
                          setSendMode(mode);
                          setSelectedIds(new Set());
                          setExcludedIds(new Set());
                        }}
                        className="text-xs"
                      >
                        {mode === "all" ? "All Users" : mode === "selected" ? "Selected Only" : "Exclude Some"}
                      </Button>
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {recipientEmails.length} recipient{recipientEmails.length !== 1 ? "s" : ""}
                  </span>
                </div>
                {sendMode === "selected" && selectedIds.size > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setSelectedIds(new Set())}>
                    <X className="w-3 h-3 mr-1" /> Clear selection ({selectedIds.size})
                  </Button>
                )}
                {sendMode === "exclude" && excludedIds.size > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setExcludedIds(new Set())}>
                    <X className="w-3 h-3 mr-1" /> Clear exclusions ({excludedIds.size})
                  </Button>
                )}
                <div className="space-y-1">
                  <Label>Subject</Label>
                  <Input value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="Email subject..." />
                </div>
                <div className="space-y-1">
                  <Label>Body (HTML supported)</Label>
                  <Textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} placeholder="Email body..." rows={5} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSendBulkEmail} disabled={sending || recipientEmails.length === 0} size="sm">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
                    Send to {recipientEmails.length} recipient{recipientEmails.length !== 1 ? "s" : ""}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBulkEmail(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No users found</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {showCheckboxes && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={
                            sendMode === "selected"
                              ? filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id))
                              : filtered.length > 0 && filtered.every((u) => excludedIds.has(u.id))
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>#</TableHead>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead>Last Sign In</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u, i) => (
                    <TableRow
                      key={u.id}
                      className={
                        sendMode === "exclude" && excludedIds.has(u.id)
                          ? "opacity-40"
                          : ""
                      }
                    >
                      {showCheckboxes && (
                        <TableCell>
                          <Checkbox
                            checked={
                              sendMode === "selected"
                                ? selectedIds.has(u.id)
                                : excludedIds.has(u.id)
                            }
                            onCheckedChange={() => toggleUser(u.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="font-medium">{formatName(u.full_name)}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell className="font-mono whitespace-nowrap text-sm">{formatPhoneNumber(u.phone)}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.source === "both"
                            ? "bg-primary/10 text-primary"
                            : u.source === "certificate"
                            ? "bg-amber-500/10 text-amber-600"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {u.source === "both" ? "Both" : u.source === "certificate" ? "Cert" : "User"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm">{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : "Never"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersTab;
